import React from 'react';

/**
 * Conjunto único de ícones do SAAC (at-55, frente A).
 *
 * ⚠️ São SVG INLINE, e isso é o ponto: hoje os 8 ícones do menu são arquivos
 * `.svg` em `src/img` carregados como `<img>`, o que os torna IMPOSSÍVEIS de
 * colorir por estado — foi por isso que o item ativo da navegação nunca pôde
 * mudar de cor. Inline, o traço usa `currentColor` e herda a cor do contexto.
 *
 * Os arquivos `.svg` continuam existindo e servindo as telas legadas
 * (`menuOpcoes.ts` ainda os importa). Aposentá-los é trabalho da frente D.
 */

export type NomeIcone =
  // menu principal — as chaves são os `id` de `views/home/menuOpcoes.ts`
  | 'cotacoes'
  | 'adesoes'
  | 'substituicao-veicular'
  | 'substituicao-titular'
  | 'vistoria'
  | 'carteira'
  | 'acompanhamento-oficina'
  | 'ti'
  // navegação e ações
  | 'inicio'
  | 'menu'
  | 'fechar'
  | 'sair'
  | 'chevron-direita'
  | 'voltar'
  // listagem (at-56, frente B)
  | 'busca'
  | 'alerta'
  | 'recarregar'
  | 'sem-dados'
  | 'mais'
  // formulário (at-58, frente C) — os 5 blocos da etapa 1 e os estados do campo
  | 'identificacao'
  | 'pessoa'
  | 'contato'
  | 'local'
  | 'endereco'
  | 'check'
  | 'chevron-baixo'
  // etapa 2 — o veículo (at-59, frente C/C2)
  | 'veiculo'
  // listagem com recorte (at-65, frente D) — o botão que abre o modal de filtros
  | 'filtro'
  // hub de administração (at-67, frente D/D5) — as 16 opções da tela `/ti`
  | 'usuario-mais'
  | 'usuario-editar'
  | 'enviar'
  | 'documento'
  | 'moeda'
  | 'relogio';

const DESENHOS: Record<NomeIcone, React.ReactNode> = {
  /* documento financeiro — proposta de preço */
  cotacoes: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </>
  ),
  /* cadastrar uma pessoa nova */
  adesoes: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </>
  ),
  /* trocar o carro */
  'substituicao-veicular': (
    <>
      <path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v9a2 2 0 0 1-2 2h-3" />
      <circle cx="7.5" cy="17.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </>
  ),
  /* trocar a pessoa dona do contrato */
  'substituicao-titular': (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <polyline points="17 11 19 13 23 9" />
    </>
  ),
  /* laudo/inspeção com fotos do veículo */
  vistoria: (
    <>
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="2" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  /* acompanhamento dos clientes do consultor */
  carteira: (
    <>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </>
  ),
  /* oficina — chave inglesa. ⚠️ Não existe no protótipo: aquela opção é da
     at-53 (08/09/2026) e é posterior a ele. */
  'acompanhamento-oficina': (
    <path d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 1 5.4-5.4l-2.6 2.6-1.4-1.4z" />
  ),
  /* painel técnico */
  ti: (
    <>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </>
  ),

  inicio: (
    <>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </>
  ),
  menu: (
    <>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </>
  ),
  fechar: <path d="M18 6 6 18M6 6l12 12" />,
  sair: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>
  ),
  'chevron-direita': <polyline points="9 18 15 12 9 6" />,
  voltar: <polyline points="15 18 9 12 15 6" />,

  /* lupa — campo de busca */
  busca: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </>
  ),
  /* triângulo de atenção — estado de erro. ⚠️ Sempre acompanhado de TEXTO:
     ícone sozinho não diz o que falhou (e a regra do projeto proíbe emoji). */
  alerta: (
    <>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L14.7 3.9a2 2 0 0 0-3.4 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  /* seta circular — "tentar novamente" */
  recarregar: (
    <>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
    </>
  ),
  /* "+" — criar registro novo */
  mais: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  /* pasta vazia — lista sem itens */
  'sem-dados': (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <line x1="9" y1="13" x2="15" y2="13" />
    </>
  ),
  /* documento de identidade — bloco "Identificação" */
  identificacao: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <line x1="7" y1="9" x2="17" y2="9" />
      <line x1="7" y1="13" x2="13" y2="13" />
    </>
  ),
  /* busto — bloco "Dados do Associado" */
  pessoa: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </>
  ),
  /* telefone — bloco "Contatos" */
  contato: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.39 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7a2 2 0 0 1 1.73 2.02z" />
  ),
  /* alfinete de mapa — bloco "Regional" */
  local: (
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  /* casa — bloco "Endereço" */
  endereco: (
    <>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </>
  ),
  /* confirmação — etapa concluída no Stepper, CPF livre no campo */
  check: <polyline points="20 6 9 17 4 12" />,
  /*
   * carro de frente para a etapa 2 (at-59).
   *
   * ⚠️ Não reusa o `substituicao-veicular`, que também desenha um carro: aquele é
   * o ícone de uma OPÇÃO DE MENU, e o item ativo da navegação o pinta. Um bloco
   * de formulário chamando o ícone da substituição faria a etapa 2 da adesão
   * anunciar visualmente um fluxo que não é o dela.
   */
  veiculo: (
    <>
      <path d="M5 17H3v-5l2.5-5.5A2 2 0 0 1 7.3 5.3h9.4a2 2 0 0 1 1.8 1.2L21 12v5h-2" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <circle cx="7.5" cy="17" r="2" />
      <circle cx="16.5" cy="17" r="2" />
    </>
  ),
  /* seta para baixo — abre seleção (o botão da regional, o Select) */
  'chevron-baixo': <polyline points="6 9 12 15 18 9" />,
  /*
   * funil — recortar uma lista.
   *
   * ⚠️ Não reusa o `busca`: buscar é procurar UM registro pelo nome; filtrar é
   * reduzir a lista inteira por um critério. As duas ações convivem lado a lado
   * na mesma barra (carteira, envios, vistoria), e dois ícones iguais ali fariam
   * o consultor clicar no errado.
   */
  filtro: <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />,

  /*
   * ───── hub de administração (at-67) ─────
   *
   * ⚠️ Os 6 abaixo substituem arquivos `.svg` que a tela `/ti` carregava como
   * `<img>` (`user-plus`, `user-pen`, `user-send`/`car-send`, `send-tracking`,
   * `coins`, `envio-acompanhamento`). Inline eles herdam a cor do contexto — é o
   * que permite o ícone acompanhar o estado do card, que com `<img>` era
   * impossível.
   */

  /* pessoa com "+" — criar usuário. Reusa o desenho de `adesoes`, e isso é
     coerente: as duas ações são "cadastrar alguém". */
  'usuario-mais': (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </>
  ),
  /* pessoa com lápis — editar usuário */
  'usuario-editar': (
    <>
      <path d="M13 21H4v-2a4 4 0 0 1 4-4h5" />
      <circle cx="10.5" cy="7" r="4" />
      <path d="M20.5 12.5 15 18l-3 1 1-3 5.5-5.5a1.8 1.8 0 0 1 2.5 2.5z" />
    </>
  ),
  /* seta saindo de uma caixa — empurrar dado para o SGA (os 2 envios e o
     acompanhamento deles) */
  enviar: (
    <>
      <path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
      <polyline points="8 8 12 4 16 8" />
      <line x1="12" y1="4" x2="12" y2="16" />
    </>
  ),
  /* folha com linhas — termos, contratos e planos (o que vira PDF) */
  documento: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="13" y2="17" />
    </>
  ),
  /* moedas empilhadas — valor do rateio */
  moeda: (
    <>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
      <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
    </>
  ),
  /* relógio — rotinas automáticas (crons) */
  relogio: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15.5 14" />
    </>
  ),
};

interface PropsIcone {
  nome: NomeIcone;
  /** Lado do quadrado em px. Padrão 20. */
  tamanho?: number;
  /** Espessura do traço. Padrão 2. */
  traco?: number;
  className?: string;
  /**
   * Texto para leitor de tela. Sem ele o ícone é `aria-hidden` — que é o certo
   * quando há rótulo textual ao lado (o caso normal no menu).
   */
  titulo?: string;
}

const Icone: React.FC<PropsIcone> = ({ nome, tamanho = 20, traco = 2, className, titulo }) => (
  <svg
    width={tamanho}
    height={tamanho}
    viewBox="0 0 24 24"
    fill="none"
    /* `currentColor` é o que permite o estado ativo mudar a cor pelo CSS do pai. */
    stroke="currentColor"
    strokeWidth={traco}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role={titulo ? 'img' : undefined}
    aria-label={titulo}
    aria-hidden={titulo ? undefined : true}
    focusable="false"
  >
    {DESENHOS[nome]}
  </svg>
);

export default Icone;
